import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  Output,
  Renderer2,
  ChangeDetectionStrategy,
} from '@angular/core';
import { GestureController, GestureDetail } from '@ionic/angular';

/** Adds a ripple effect *and* distinguishes between long-press and tap gestures according to a time threshold.
 *  Similar to ion-ripple-effect component, except gets rid of some dev dependencies on parent element(s).
 *  Developer does not need to manually add ion-activatable and nty-ripple-parent classes to the parent element.
 *
 * @link https://ionicframework.com/docs/api/ripple-effect
 *
 * @warning Do not use *with* ion-ripple-effect component. This *replaces* ion-ripple-effect for our needs.
 * @warning Potential for unexpected results when mixing (click) bindings with (tap) and (press) bindings.
 * @warning Do not use *with* ntyPress. The ntyPress gesture will conflict with the gesture generated here.
 * */
@Component({
  selector: 'nty-ripple',
  template: ``,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: `
    $scale-duration: 225ms;
    $fade-in-duration: 75ms;
    $fade-out-duration: 150ms;

    :host {
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;

      position: absolute;

      contain: strict;
      pointer-events: none;

      &[type='unbounded'] {
        contain: layout size style;
      }
    }

    ::ng-deep {
      .nty-ripple-parent {
        position: relative;
        overflow: hidden;
        cursor: pointer;
      }

      .nty-ripple-effect {
        border-radius: 50%;

        position: absolute;

        background-color: currentColor;
        color: inherit;

        contain: strict;
        opacity: 0;
        animation:
          $scale-duration rippleAnimation forwards,
          $fade-in-duration fadeInAnimation forwards;

        will-change: transform, opacity;
        pointer-events: none;
      }

      .nty-ripple-fade-out {
        transform: translate(var(--translate-end)) scale(var(--final-scale, 1));
        animation: $fade-out-duration fadeOutAnimation forwards;
      }

      @keyframes rippleAnimation {
        from {
          animation-timing-function: cubic-bezier(0.4, 0, 0.2, 1);

          transform: scale(1);
        }

        to {
          transform: translate(var(--translate-end)) scale(var(--final-scale, 1));
        }
      }

      @keyframes fadeInAnimation {
        from {
          animation-timing-function: linear;

          opacity: 0;
        }

        to {
          opacity: 0.16;
        }
      }

      @keyframes fadeOutAnimation {
        from {
          animation-timing-function: linear;

          opacity: 0.16;
        }

        to {
          opacity: 0;
        }
      }
    }
  `,
})
export class RippleComponent implements AfterViewInit, OnDestroy {
  private readonly rippleAnimationTime = 325;
  private readonly rippleFadeOutTime = 200;
  private readonly rippleOriginScale = 0.5;
  private readonly ripplePadding = 10;
  private readonly gestureName = 'nty-ripple-press';
  private readonly host = inject(ElementRef);
  private readonly gesture = inject(GestureController);
  private readonly renderer = inject(Renderer2);
  private readonly cdr = inject(ChangeDetectorRef);

  private isLongPressing = false;
  private hasLongPressed = false;
  private timeout?: NodeJS.Timeout;

  /** A tap gesture is emitted when the gesture is below the given time threshold. */
  @Output() readonly tap = new EventEmitter();

  /** A long-press gesture is emitted when the gesture is at or above the given time threshold. */
  @Output() readonly press = new EventEmitter();

  /** Specifies how long (in milliseconds) is the threshold for detecting a long-press as opposed to a tap gesture. */
  @Input() time = 650;

  /** A type of 'bounded' will expand the ripple effect from the click position outwards.
   *  A type of 'unbounded' will expand the ripple effect from the center of the parent element. */
  @Input() type: 'bounded' | 'unbounded' = 'bounded';

  /** Automatically add needed CSS classes to parent element. */
  @Input() styleParent = true;

  ngAfterViewInit(): void {
    // We will add needed CSS classes & gesture definition to the *parent* of the host.
    const parent = this.getParentOfHost();

    if (this.styleParent) {
      // See: apps/mobile-app/src/styles/components/ripple.component.scss
      this.renderer.addClass(parent, 'nty-ripple-parent');

      // See: https://ionicframework.com/docs/api/ripple-effect
      this.renderer.addClass(parent, 'ion-activatable');
    }

    // Gesture Definition
    this.gesture
      .create({
        el: parent,
        threshold: 0,
        gestureName: this.gestureName,
        passive: true,
        onStart: (detail: GestureDetail) => this.detectLongPress(detail.startX, detail.startY),
        onEnd: () => this.endPress(),
        onMove: () => this.resetLongPress(), // If user is dragging, user is not pressing
      })
      .enable(true);
  }

  ngOnDestroy(): void {
    this.resetLongPress();
  }

  /** On start, we assume intention to long-press. After timeout, long-press is emitted. */
  private detectLongPress(x: number, y: number) {
    this.addRippleAnimation(x, y);
    this.resetLongPress(true);
    this.timeout = setTimeout(() => {
      if (!this.isLongPressing) return;
      this.press.emit();
      this.cdr.markForCheck();
      this.resetLongPress(false, true);
    }, this.time);
  }

  /** If user has not long-pressed, user has tapped */
  private endPress() {
    if (!this.hasLongPressed) this.tap.emit();
    this.resetLongPress();
  }

  /** Sets/Resets long-press detection values. */
  private resetLongPress(isLongPressing = false, hasLongPressed = false) {
    if (this.timeout) {
      clearInterval(this.timeout);
      this.timeout = undefined;
    }
    this.isLongPressing = isLongPressing;
    this.hasLongPressed = hasLongPressed;
  }

  /** Ripple animation is accomplished via CSS and addition of child elements. */
  private addRippleAnimation(x: number, y: number) {
    const { height, left, top, width } = this.getHostRect();
    const hypotenuse = Math.sqrt(width * width + height * height);
    const maxDim = Math.max(height, width);
    const unbounded = this.type === 'unbounded';
    const maxRadius = unbounded ? maxDim : hypotenuse + this.ripplePadding;
    const initialSize = Math.floor(maxDim * this.rippleOriginScale);
    const finalScale = maxRadius / initialSize;
    const posX = unbounded ? width * 0.5 : x - left;
    const posY = unbounded ? height * 0.5 : y - top;
    const styleX = posX - initialSize * 0.5;
    const styleY = posY - initialSize * 0.5;
    const moveX = width * 0.5 - posX;
    const moveY = height * 0.5 - posY;

    const rippleDiv = this.renderer.createElement('div');
    rippleDiv.classList.add('nty-ripple-effect'); // See: apps/mobile-app/src/styles/components/ripple.component.scss
    rippleDiv.style.setProperty('height', `${initialSize}px`);
    rippleDiv.style.setProperty('left', `${styleX}px`);
    rippleDiv.style.setProperty('top', `${styleY}px`);
    rippleDiv.style.setProperty('width', `${initialSize}px`);
    rippleDiv.style.setProperty('--final-scale', `${finalScale}`);
    rippleDiv.style.setProperty('--translate-end', `${moveX}px, ${moveY}px`);

    this.renderer.appendChild(this.getHost(), rippleDiv);

    setTimeout(() => this.removeRippleElement(rippleDiv), this.rippleAnimationTime);
  }

  /** Ripple ends in a CSS fade animation and removal of the added child elements. */
  private removeRippleElement(ripple: HTMLElement) {
    ripple.classList.add('nty-ripple-fade-out'); // See: apps/mobile-app/src/styles/components/ripple.component.scss
    setTimeout(() => this.renderer.removeChild(this.getHost(), ripple), this.rippleFadeOutTime);
  }

  /** Bounds are used to calculate the ripple effect. */
  private getHostRect(): DOMRect {
    return this.host.nativeElement.getBoundingClientRect();
  }

  /** Ripple gets appended to the host element or shadow. */
  private getHost(): HTMLElement {
    return this.host.nativeElement.shadowRoot || this.host.nativeElement;
  }

  /** The gesture needs to be tied to the *parent* of the nty-ripple element. */
  private getParentOfHost(): HTMLElement {
    return this.renderer.parentNode(this.host.nativeElement);
  }
}
